import { PlanLimits, FREE_LIMITS, PRO_LIMITS, TournamentType } from '../models';
import { SubscriptionRepository } from '../repositories';
import { logger } from '@/src/core/utils';

export type FeatureCheck =
  | { feature: 'create_tournament' }
  | { feature: 'tournament_type'; tournamentType: TournamentType }
  | { feature: 'add_participant'; currentCount: number }
  | { feature: 'create_league' }
  | { feature: 'league_tournament'; currentCount: number }
  | { feature: 'private_tournament' }
  | { feature: 'export_pdf' }
  | { feature: 'create_team' };

export interface FeatureAccessResult {
  allowed: boolean;
  reason?: string;
  limit?: number;
  current?: number;
}

export class CheckFeatureAccessUseCase {
  constructor(private subscriptionRepository: SubscriptionRepository) {}

  async execute(userId: string, check: FeatureCheck): Promise<FeatureAccessResult> {
    logger.debug('CheckFeatureAccess', 'Checking access', { userId, feature: check.feature });

    const subscription = await this.subscriptionRepository.getSubscription(userId);
    const limits: PlanLimits = subscription?.plan === 'pro' ? PRO_LIMITS : FREE_LIMITS;

    switch (check.feature) {
      case 'create_tournament': {
        const count = await this.subscriptionRepository.countActiveTournaments(userId);
        if (count >= limits.maxActiveTournaments) {
          return {
            allowed: false,
            reason: 'tournament_limit_reached',
            limit: limits.maxActiveTournaments,
            current: count,
          };
        }
        return { allowed: true };
      }

      case 'tournament_type': {
        if (!limits.allowedTournamentTypes.includes(check.tournamentType)) {
          return {
            allowed: false,
            reason: 'tournament_type_not_allowed',
          };
        }
        return { allowed: true };
      }

      case 'add_participant': {
        if (check.currentCount >= limits.maxParticipantsPerTournament) {
          return {
            allowed: false,
            reason: 'participant_limit_reached',
            limit: limits.maxParticipantsPerTournament,
            current: check.currentCount,
          };
        }
        return { allowed: true };
      }

      case 'create_league': {
        const count = await this.subscriptionRepository.countActiveLeagues(userId);
        if (count >= limits.maxActiveLeagues) {
          return {
            allowed: false,
            reason: 'league_limit_reached',
            limit: limits.maxActiveLeagues,
            current: count,
          };
        }
        return { allowed: true };
      }

      case 'league_tournament': {
        if (check.currentCount >= limits.maxTournamentsPerLeague) {
          return {
            allowed: false,
            reason: 'league_tournament_limit_reached',
            limit: limits.maxTournamentsPerLeague,
            current: check.currentCount,
          };
        }
        return { allowed: true };
      }

      case 'private_tournament': {
        const count = await this.subscriptionRepository.countPrivateTournaments(userId);
        if (count >= limits.maxPrivateTournaments) {
          return {
            allowed: false,
            reason: 'private_tournament_limit_reached',
            limit: limits.maxPrivateTournaments,
            current: count,
          };
        }
        return { allowed: true };
      }

      case 'export_pdf': {
        if (!limits.canExportPdf) {
          return {
            allowed: false,
            reason: 'pdf_export_pro_only',
          };
        }
        return { allowed: true };
      }

      case 'create_team': {
        const count = await this.subscriptionRepository.countActiveTeams(userId);
        if (count >= limits.maxActiveTeams) {
          return {
            allowed: false,
            reason: 'team_limit_reached',
            limit: limits.maxActiveTeams,
            current: count,
          };
        }
        return { allowed: true };
      }

      default:
        return { allowed: true };
    }
  }
}
